import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider } from "./contexts/AuthContext";
import { AppLayout } from "./layouts/AppLayout";
import { AdminRoute } from "./components/AdminRoute";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy-loaded pages for code splitting
const Overview = lazy(() => import("./pages/Overview"));
const CryptoAgent = lazy(() => import("./pages/CryptoAgent"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AssetView = lazy(() => import("./pages/AssetView"));
const Valuation = lazy(() => import("./pages/Valuation"));
const DeFiProtocols = lazy(() => import("./pages/DeFiProtocols"));
const Auth = lazy(() => import("./pages/Auth"));
const TelegramSetup = lazy(() => import("./pages/TelegramSetup"));
const AlphaOnChain = lazy(() => import("./pages/AlphaOnChain"));
const Alerts = lazy(() => import("./pages/Alerts"));
const Settings = lazy(() => import("./pages/Settings"));
const AdminCoverage = lazy(() => import("./pages/AdminCoverage"));
const AdminAIAnalyst = lazy(() => import("./pages/AdminAIAnalyst"));
const AdminKnowledge = lazy(() => import("./pages/AdminKnowledge"));
const MacroBTC = lazy(() => import("./pages/MacroBTC"));
const AlphaQuant = lazy(() => import("./pages/AlphaQuant"));
const AlphaQuantStrategyConfig = lazy(() => import("./pages/AlphaQuantStrategyConfig"));
const Stablecoins = lazy(() => import("./pages/Stablecoins"));
const Macro = lazy(() => import("./pages/Macro"));
const Chart = lazy(() => import("./pages/Chart"));
const Landing = lazy(() => import("./pages/Landing"));
const MarketMonitor = lazy(() => import("./pages/MarketMonitor"));

const PageFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <Skeleton className="h-8 w-48" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      staleTime: 120000,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              {/* Landing page - outside main layout */}
              <Route path="/" element={<Landing />} />

              {/* Auth routes - outside main layout */}
              <Route path="/auth" element={<Auth />} />

              {/* Main app routes with layout */}
              <Route path="/app" element={<AppLayout />}>
                <Route index element={<Navigate to="/app/overview" replace />} />
                <Route path="overview" element={<Overview />} />
                <Route path="markets" element={<CryptoAgent />} />

                <Route path="valuation" element={<Valuation />} />
                <Route path="defi-protocols" element={<DeFiProtocols />} />
                <Route path="stablecoins" element={<Stablecoins />} />

                {/* Admin-only routes */}
                <Route path="alpha-onchain" element={<AdminRoute><AlphaOnChain /></AdminRoute>} />
                <Route path="alpha-quant" element={<AdminRoute><AlphaQuant /></AdminRoute>} />
                <Route path="alpha-quant/strategy" element={<AdminRoute><AlphaQuantStrategyConfig /></AdminRoute>} />
                <Route path="macro" element={<AdminRoute><Macro /></AdminRoute>} />
                <Route path="admin/coverage" element={<AdminRoute><AdminCoverage /></AdminRoute>} />
                <Route path="admin/ai-analyst" element={<AdminRoute><AdminAIAnalyst /></AdminRoute>} />
                <Route path="admin/knowledge" element={<AdminRoute><AdminKnowledge /></AdminRoute>} />

                {/* Public routes */}
                <Route path="macro-btc" element={<MacroBTC />} />
                <Route path="macro-indicators" element={<Navigate to="/app/macro-btc" replace />} />
                <Route path="market-monitor" element={<MarketMonitor />} />
                <Route path="settings" element={<Settings />} />
                <Route path="asset/:symbol" element={<AssetView />} />

                {/* Legacy redirects */}
                <Route path="telegram-setup" element={<Navigate to="/app/settings" replace />} />
              </Route>

              {/* Chart page - with sidebar layout */}
              <Route path="/chart" element={<AppLayout />}>
                <Route index element={<Chart />} />
              </Route>

              {/* Catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
